import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const UOMS = ['EA', 'FT', 'M', 'LB', 'KG', 'GAL', 'L'];
const sel = 'w-full h-9 border border-slate-200 rounded-sm px-2 text-sm bg-white';

// Quick-create an inventory item from a PO line; vendor/description/cost prefilled from the PO.
export const QuickAddItemDialog = ({ api, open, onOpenChange, vendorId, seed, onCreated }) => {
  const [meta, setMeta] = useState({ cats: [], types: [] });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api().get('/item-categories?active_only=true').catch(() => ({ data: [] })), api().get('/item-types').catch(() => ({ data: [] }))])
      .then(([c, t]) => setMeta({ cats: c.data || [], types: t.data || [] }));
    setForm({ item_sku: '', item_name: seed?.description || '', item_category: '', item_type: 'Component', base_uom: 'EA', vendor_sku: '', purchase_cost: seed?.unit_cost || '', reorder_point: '', hs_code: seed?.hs_code || '', country_of_origin: seed?.country_of_origin || '', eccn: seed?.eccn || '' });
  }, [open, api, seed]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.item_sku.trim() || !form.item_name.trim() || !form.item_category) { toast.error('SKU, name and category are required'); return; }
    setSaving(true);
    try {
      const r = await api().post('/items', { ...form, item_sku: form.item_sku.trim(), item_name: form.item_name.trim(), vendor_sku: form.vendor_sku.trim() || null, description: seed?.long_description || null,
        hs_code: form.hs_code.trim() || null, eccn: form.eccn.trim() || null, country_of_origin: form.country_of_origin.trim().toUpperCase() || null,
        purchase_cost: form.purchase_cost === '' ? null : Number(form.purchase_cost), reorder_point: Number(form.reorder_point) || 0, preferred_vendor_id: vendorId || null });
      toast.success(`${r.data.item_sku} added to inventory`);
      onCreated(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create item'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="po-quick-add-item-dialog">
        <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>SKU *</Label><Input value={form.item_sku} onChange={set('item_sku')} className="rounded-sm mono" autoFocus data-testid="qai-sku" /></div>
          <div><Label>Vendor SKU</Label><Input value={form.vendor_sku} onChange={set('vendor_sku')} className="rounded-sm mono" data-testid="qai-vendor-sku" /></div>
          <div className="col-span-2"><Label>Item Name *</Label><Input value={form.item_name} onChange={set('item_name')} className="rounded-sm" data-testid="qai-name" /></div>
          <div><Label>Category *</Label>
            <select value={form.item_category} onChange={set('item_category')} className={sel} data-testid="qai-category">
              <option value="">— select —</option>{meta.cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div><Label>Type *</Label>
            <select value={form.item_type} onChange={set('item_type')} className={sel} data-testid="qai-type">
              {meta.types.length === 0 ? <option value="Component">Component</option> : meta.types.map(t => <option key={t.id} value={t.name}>{t.label || t.name}</option>)}
            </select>
          </div>
          <div><Label>Unit of Measure *</Label>
            <select value={form.base_uom} onChange={set('base_uom')} className={sel} data-testid="qai-uom">{UOMS.map(u => <option key={u}>{u}</option>)}</select>
          </div>
          <div><Label>Purchase Cost{form.item_type === 'Service' ? '' : ' *'}</Label><Input type="number" min="0" step="0.01" value={form.purchase_cost} onChange={set('purchase_cost')} className="rounded-sm mono" data-testid="qai-cost" /></div>
          <div><Label>Commodity Code (HS)</Label><Input value={form.hs_code} onChange={set('hs_code')} className="rounded-sm mono" data-testid="qai-hs" /></div>
          <div><Label>ECCN</Label><Input value={form.eccn} onChange={set('eccn')} placeholder="e.g. EAR99" className="rounded-sm mono" data-testid="qai-eccn" /></div>
          <div><Label>Country of Origin (code)</Label><Input value={form.country_of_origin} onChange={set('country_of_origin')} placeholder="e.g. CA" maxLength={40} className="rounded-sm mono" data-testid="qai-coo" /></div>
          <div><Label>Reorder Point</Label><Input type="number" min="0" value={form.reorder_point} onChange={set('reorder_point')} className="rounded-sm mono" data-testid="qai-reorder" /></div>
        </div>
        <p className="text-[11px] text-slate-500">Starts at 0 on hand. Stock is added when this PO is received. The PO vendor is set as preferred vendor.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="qai-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="qai-save">{saving ? 'Saving…' : 'Save & Use on Line'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddItemDialog;
